import React from 'react';
import {
    Box,
    FormControl,
    FormControlLabel,
    MenuItem,
    Rating,
    Select,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import MdEditor from "crqlar-react-common/dist/components/MdEditor";
import {RatingQuestion} from "crqlar-react-common/dist/models/forms/form";
import {useTranslation} from "react-i18next";

interface RatingInputProps {
    question: RatingQuestion;
    onQuestionUpdate: (question: RatingQuestion) => void;
    isSelected?: boolean;
    onSelect?: () => void;
}

const RatingInput: React.FC<RatingInputProps> = ({
                                                     question,
                                                     onQuestionUpdate,
                                                     isSelected = false,
                                                     onSelect
                                                 }) => {
    const {t} = useTranslation();

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            title: e.target.value
        });
    };

    const handleDescriptionChange = (value?: string) => {
        onQuestionUpdate({
            ...question,
            description: value || ''
        });
    };

    const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            required: e.target.checked
        });
    };

    const handleMaxRatingChange = (e: any) => {
        onQuestionUpdate({
            ...question,
            maxRating: e.target.value as number
        });
    };

    const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            ratingLabel: e.target.value
        });
    };

    const maxRating = question.maxRating || 10;

    return (
        <Stack
            onClick={onSelect}
            sx={{
                p: 3,
                borderRadius: 1,
                border: isSelected ? '2px solid' : '1px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    borderColor: 'primary.light',
                    backgroundColor: 'action.hover'
                },
                position: 'relative'
            }}
        >
            <Box sx={{alignSelf: 'flex-end'}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={question.required || false}
                            onChange={handleRequiredChange}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                        />
                    }
                    label={<Typography variant="caption">{t('Required')}</Typography>}
                    labelPlacement="start"
                    onClick={(e) => e.stopPropagation()}
                    sx={{m: 0}}
                />
            </Box>

            <TextField
                fullWidth
                label={t('Question Label')}
                value={question.title}
                onChange={handleTitleChange}
                variant="standard"
                sx={{mb: 2, pr: 12}}
                onClick={(e) => e.stopPropagation()}
            />

            {isSelected ? (
                <Box sx={{mb: 2}} onClick={(e) => e.stopPropagation()}>
                    <MdEditor
                        value={question.description || ''}
                        onChange={handleDescriptionChange}
                        placeholder={t('Question Description (optional)')}
                        className={'mini-mdx'}
                    />
                </Box>
            ) : (
                question.description && (
                    <Box sx={{mb: 2}}>
                        <MdRenderer>
                            {question.description}
                        </MdRenderer>
                    </Box>
                )
            )}

            <TextField
                fullWidth
                label={t('Rating Label (optional)')}
                value={question.ratingLabel || ''}
                onChange={handleLabelChange}
                variant="standard"
                placeholder={t('e.g., Energy Level, Mood, Intensity')}
                sx={{mb: 2}}
                onClick={(e) => e.stopPropagation()}
            />

            <FormControl variant="standard" sx={{mb: 3}} onClick={(e) => e.stopPropagation()}>
                <Typography variant="caption" sx={{mb: 1}}>{t('Maximum Rating')}</Typography>
                <Select
                    value={maxRating}
                    onChange={handleMaxRatingChange}
                    size="small"
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                bgcolor: '#fff',
                            },
                        },
                    }}
                >
                    <MenuItem value={5}>{t('5 (Stars)')}</MenuItem>
                    <MenuItem value={10}>{t('10 (Scale)')}</MenuItem>
                </Select>
            </FormControl>

            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                {maxRating === 5 ? (
                    <Rating
                        value={0}
                        max={5}
                        readOnly
                        emptyIcon={<StarIcon style={{opacity: 0.3}} fontSize="inherit"/>}
                    />
                ) : (
                    <Box sx={{display: 'flex', gap: 0.5}}>
                        {Array.from({length: 10}, (_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    color: 'text.disabled'
                                }}
                            >
                                {i + 1}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Stack>
    );
};

export default RatingInput;
